import { Component, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FikoYT3dLineDialogComponent } from './fikoyt-3d-line-dialog.component';
import { FikoYT3dLine } from './fikoyt-3d-line';
import { FikoSahne } from '../../FikoSahne';

/**
 * 3D Line Toolbar Button Component
 *
 * Araç çubuğuna eklenebilen 3D çizgi oluşturma butonu.
 * Tıklandığında çizgi oluşturma dialogunu açar.
 *
 * Kullanım:
 * ```html
 * <fikoyt-3d-line-toolbar-button
 *   [sahne]="fikoSahne"
 *   (cizgiOlusturuldu)="handleCizgi($event)">
 * </fikoyt-3d-line-toolbar-button>
 * ```
 */
@Component({
    selector: 'fikoyt-3d-line-toolbar-button',
    template: `
        <!-- Toolbar Button -->
        <button class="fiko-3d-line-btn"
                [class.active]="dialogAcik"
                (click)="dialogAcKapa()"
                title="3D Çizgi Oluştur">
            <!-- SVG Icon - 3D Line -->
            <svg viewBox="0 0 24 24"
                 width="18"
                 height="18"
                 fill="none"
                 stroke="currentColor"
                 stroke-width="2"
                 stroke-linecap="round"
                 stroke-linejoin="round">
                <!-- Ana çizgi -->
                <line x1="4" y1="20" x2="20" y2="4"/>
                <!-- Başlangıç noktası -->
                <circle cx="4" cy="20" r="2.5" fill="currentColor" stroke="none"/>
                <!-- Bitiş noktası -->
                <circle cx="20" cy="4" r="2.5" fill="currentColor" stroke="none"/>
                <!-- 3D efekt çizgileri -->
                <line x1="6" y1="20" x2="8" y2="22" opacity="0.5"/>
                <line x1="20" y1="6" x2="22" y2="8" opacity="0.5"/>
            </svg>
        </button>

        <!-- Dialog Component -->
        <fikoyt-3d-line-dialog
            #lineDialog
            [sahne]="sahne"
            [gorunur]="dialogAcik"
            (cizgiOlusturuldu)="cizgiOlusturulduHandler($event)"
            (kapandi)="dialogKapandiHandler()">
        </fikoyt-3d-line-dialog>
    `,
    styles: [`
        :host {
            display: inline-block;
        }

        .fiko-3d-line-btn {
            width: 28px;
            height: 28px;
            border: 1px solid rgba(0, 255, 255, 0.3);
            border-radius: 6px;
            background: linear-gradient(135deg, rgba(0, 60, 80, 0.8), rgba(0, 40, 50, 0.9));
            color: rgba(0, 255, 255, 0.9);
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.2s ease;
            padding: 0;

            &:hover {
                background: linear-gradient(135deg, rgba(0, 80, 100, 0.9), rgba(0, 60, 70, 0.95));
                border-color: rgba(0, 255, 255, 0.6);
                box-shadow: 0 0 12px rgba(0, 255, 255, 0.4);
                transform: scale(1.05);

                svg {
                    filter: drop-shadow(0 0 4px rgba(0, 255, 255, 0.6));
                }
            }

            &:active {
                transform: scale(0.98);
            }

            &.active {
                background: linear-gradient(135deg, rgba(0, 120, 140, 0.95), rgba(0, 80, 100, 0.95));
                border-color: rgba(0, 255, 255, 0.8);
                box-shadow: 0 0 15px rgba(0, 255, 255, 0.5);
                color: #00ffff;

                svg {
                    filter: drop-shadow(0 0 6px rgba(0, 255, 255, 0.8));
                }
            }

            svg {
                transition: filter 0.2s ease;
            }
        }
    `],
    standalone: true,
    imports: [CommonModule, FikoYT3dLineDialogComponent]
})
export class FikoYT3dLineToolbarButtonComponent {

    /** Sahne referansı */
    @Input() sahne?: FikoSahne;

    /** Çizgi oluşturulduğunda emit */
    @Output() cizgiOlusturuldu = new EventEmitter<FikoYT3dLine>();

    /** Dialog referansı */
    @ViewChild('lineDialog') lineDialog?: FikoYT3dLineDialogComponent;

    /** Dialog açık mı? */
    dialogAcik: boolean = false;

    /**
     * Dialog aç/kapa toggle
     */
    dialogAcKapa(): void {
        this.dialogAcik = !this.dialogAcik;
        if (this.dialogAcik && this.lineDialog) {
            this.lineDialog.ac();
        }
    }

    /**
     * Çizgi oluşturulduğunda
     */
    cizgiOlusturulduHandler(cizgi: FikoYT3dLine): void {
        this.cizgiOlusturuldu.emit(cizgi);
        this.dialogAcik = false;

        // Sahneye ekle
        if (this.sahne) {
            this.sahne.add(cizgi);
        }
    }

    /**
     * Dialog kapandığında
     */
    dialogKapandiHandler(): void {
        this.dialogAcik = false;
    }
}

/**
 * 3D Line SVG Icon (standalone kullanım için)
 *
 * Sadece SVG ikonunu döndürür, toolbar dışında kullanım için.
 */
export const LINE_3D_ICON_SVG = `
<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <line x1="4" y1="20" x2="20" y2="4"/>
    <circle cx="4" cy="20" r="2.5" fill="currentColor" stroke="none"/>
    <circle cx="20" cy="4" r="2.5" fill="currentColor" stroke="none"/>
    <line x1="6" y1="20" x2="8" y2="22" opacity="0.5"/>
    <line x1="20" y1="6" x2="22" y2="8" opacity="0.5"/>
</svg>
`;
